import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDashboardTheme } from './themeHelper';
import { ChevronLeft, ChevronRight, Search, RotateCcw, Calendar, Clock, Repeat, Target, Sparkles, Bot } from 'lucide-react';

interface AllPracticeSessionsPageProps {
  onBack: () => void;
  userData?: any;
  rawMeetings?: any[];
}

// Top-level classification: Scenario / Digital Twin / Pitch
export const isScenarioSession = (m: any): boolean => {
  const type = String(m.meetingType || m.otherDetails?.meetingType || m.type || '').toLowerCase();
  const title = String(m.pitchTitle || m.title || m.scenarioTitle || '').toLowerCase();
  return m.isScenario === true || type === 'scenario' || title.includes('scenario');
};

export const isDigitalTwinPitch = (m: any): boolean => {
  if (isScenarioSession(m)) return false;
  const title = String(m.pitchTitle || m.otherDetails?.meetingType || m.meetingType || m.templateTitle || m.title || '').toLowerCase();
  const cat = String(m.category || '').toLowerCase();
  const scriptType = String(m.scriptType || '').toLowerCase();
  return (
    m.isDigitalTwin === true ||
    m.digitalTwin === true ||
    !!m.digitalTwinTemplateId ||
    !!m.templateId ||
    title.includes('digital twin') ||
    title.includes('digital-twin') ||
    title.includes('twin') ||
    cat.includes('twin') ||
    scriptType.includes('twin')
  );
};

export const isPitchSession = (m: any): boolean => {
  return !isScenarioSession(m) && !isDigitalTwinPitch(m);
};

// Score color and status label logic matching Meeting tab & Rep Dashboard
const getSemanticColor = (score: number, isDark: boolean = true) => {
  if (score >= 75) return isDark ? '#B8F890' : '#5a9d50';
  if (score >= 50) return isDark ? '#F5B84C' : '#d97706';
  return isDark ? '#F17272' : '#dc2626';
};

const getStatusLabel = (score: number) => {
  if (score >= 85) return 'Excellent';
  if (score >= 70) return 'Good';
  if (score >= 50) return 'Average';
  return 'Needs Work';
};

const formatDuration = (val?: any): string => {
  if (val === null || val === undefined || val === '') return '—';
  if (typeof val === 'string') {
    if (val.includes(':')) return val;
    const num = parseFloat(val);
    if (!isNaN(num) && num > 0) val = num;
    else return '—';
  }
  const numVal = Number(val);
  if (isNaN(numVal) || numVal <= 0) return '—';
  const totalSeconds = numVal > 10800 ? Math.round(numVal / 1000) : Math.round(numVal);
  const m = Math.floor(totalSeconds / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

export const getItemDuration = (item: any): string => {
  if (!item) return '—';

  // 1. Direct seconds duration
  const directSecs = item.duration ?? item.durationSeconds ?? item.duration_seconds ?? item.callDurationSeconds ?? item.audioDurationSeconds ?? item.callDuration ?? item.audioDuration ?? item.totalDuration;
  if (directSecs !== undefined && directSecs !== null && Number(directSecs) > 0) {
    return formatDuration(directSecs);
  }

  // 2. TranscriptJson segments end time (exact audio/pitch duration)
  let transcript = item.transcriptJson;
  if (typeof transcript === 'string') {
    try { transcript = JSON.parse(transcript); } catch {}
  }
  if (Array.isArray(transcript) && transcript.length > 0) {
    const last = transcript[transcript.length - 1];
    const end = last?.end ?? last?.endTime ?? last?.end_time ?? last?.offset;
    if (typeof end === 'number' && end > 0) {
      return formatDuration(end);
    }
  }

  // 3. Conversation Analytics / AI Feedback duration
  const caDuration = item.conversationAnalytics?.duration ?? item.conversation_analytics?.duration ?? item.conversationAnalytics?.callDuration ?? item.conversation_analytics?.callDuration ?? item.aiFeedback?.duration ?? item.aiFeedback?.sessionDuration;
  if (caDuration !== undefined && caDuration !== null && Number(caDuration) > 0) {
    return formatDuration(caDuration);
  }

  // 4. Session timestamps: sessionEnd - sessionStart
  if (item.sessionEnd && item.sessionStart) {
    const start = new Date(item.sessionStart).getTime();
    const end = new Date(item.sessionEnd).getTime();
    if (!isNaN(start) && !isNaN(end) && end > start) {
      return formatDuration((end - start) / 1000);
    }
  }

  // 5. Duration in minutes
  const mins = item.durationMinutes ?? item.duration_minutes ?? item.avgDurationMin ?? item.scenario?.avgDurationMin ?? item.scenario?.durationMinutes;
  if (mins !== undefined && mins !== null && Number(mins) > 0) {
    return formatDuration(Number(mins) * 60);
  }

  // 6. Audio/Video metadata
  if (item.audioMetadata?.duration || item.videoMetadata?.duration) {
    return formatDuration(item.audioMetadata?.duration || item.videoMetadata?.duration);
  }

  return '—';
};

const getRepName = (item: any, userData?: any): string => {
  if (item.salesRep && typeof item.salesRep === 'string' && item.salesRep.trim()) {
    return item.salesRep.trim();
  }
  if (item.userName && typeof item.userName === 'string' && item.userName.trim()) {
    return item.userName.trim();
  }
  if (item.fullName && typeof item.fullName === 'string' && item.fullName.trim()) {
    return item.fullName.trim();
  }
  if (item.name && typeof item.name === 'string' && item.name.trim()) {
    return item.name.trim();
  }
  if (item.repName && typeof item.repName === 'string' && item.repName.trim()) {
    return item.repName.trim();
  }
  if (item.createdBy && typeof item.createdBy === 'string' && item.createdBy.includes(' ')) {
    return item.createdBy.trim();
  }

  // Lookup by ID in userData
  const uid = item.userId || item.user_id || item.repId || item.createdBy || item.ownerId || item.id;
  if (uid && userData?.data) {
    const d = userData.data;
    const userSummary = Array.isArray(d.userPitchSummary) ? d.userPitchSummary : (d.userPitchSummary ? Object.values(d.userPitchSummary) : []);
    const matchSummary = userSummary.find((x: any) => String(x.userId || x.id) === String(uid));
    if (matchSummary) {
      const name = matchSummary.name || matchSummary.userName || `${matchSummary.firstName || ''} ${matchSummary.lastName || ''}`.trim() || matchSummary.email;
      if (name) return name;
    }

    if (d.users && d.users[uid]) {
      const u = d.users[uid];
      const name = u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;
      if (name) return name;
    }
    if (Array.isArray(d.tenantUserList?.items)) {
      const u = d.tenantUserList.items.find((x: any) => String(x.id || x.userId) === String(uid));
      if (u) {
        const name = u.name || u.fullName || u.userName || `${u.firstName || u.first_name || ''} ${u.lastName || u.last_name || ''}`.trim() || u.email;
        if (name) return name;
      }
    }
    if (Array.isArray(d.repScriptStats)) {
      const u = d.repScriptStats.find((x: any) => String(x.userId) === String(uid));
      if (u && u.userName) return u.userName;
    }
  }

  return 'Sales Representative';
};

const PersonaPracticeCard: React.FC<{
  item: any;
  userData?: any;
  onNavigate: (item: any) => void;
  colors: any;
  isDark: boolean;
}> = ({ item, userData, onNavigate, colors, isDark }) => {
  const contact = item.companyContact || item.customerData;
  const fullName = contact ? `${contact.firstName || contact.first_name || ''} ${contact.lastName || contact.last_name || ''}`.trim() : '';
  const repName = getRepName(item, userData);
  const displayName = fullName || repName;

  const isScenario = isScenarioSession(item);
  const isDigitalTwin = isDigitalTwinPitch(item);
  const sessionTypeLabel = isDigitalTwin ? 'Digital Twin' : (isScenario ? 'Guided Scenario' : 'Sales Pitch');
  const sessionIcon = isDigitalTwin ? <Bot className="w-4 h-4 text-purple-400" /> : (isScenario ? <Target className="w-4 h-4 text-cyan-400" /> : <Sparkles className="w-4 h-4 text-[#B8F890]" />);

  const email = contact?.email || item.email || item.user_id || `${repName.toLowerCase().replace(/\s+/g, '.')}@pitchster.com`;
  const company = item.company || item.companyName || contact?.companyName || item.TargetCompanyProfile?.companyName || (isScenario ? 'Interactive Simulation' : (isDigitalTwin ? 'AI Digital Avatar' : 'Sales Pitch Session'));
  const industry = item.industry || contact?.industry || item.TargetCompanyProfile?.industry || (isScenario ? 'Scenario Training' : (isDigitalTwin ? 'Avatar Coaching' : 'Pitch Engine'));
  const businessModel = item.businessModel || contact?.businessModel || (isScenario ? 'AI Roleplay' : 'Practice');
  const designation = contact?.jobTitle || contact?.designation || item.designation || item.jobTitle || 'Sales Executive';

  const scoreVal = Number(item.score ?? item.overallScore ?? item.overallPercentage ?? item.avargerscroing ?? item.avgScore ?? 0);
  const scoreColor = getSemanticColor(scoreVal, isDark);
  const scoreLabel = getStatusLabel(scoreVal);

  const dateObj = item.created_at || item.createdAt || item.date ? new Date(item.created_at || item.createdAt || item.date) : new Date();
  const dateStr = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const timeStr = dateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  const durationStr = getItemDuration(item);

  const salesStage = item.salesCycleStage || item.stageOfSalesCycle || item.stage || item.salesStage || (isScenario ? 'Interactive Practice' : (isDigitalTwin ? 'Simulation Pitch' : 'Pitch Practice'));
  const contactType = item.typeOfContact || item.contactType || (isScenario ? 'AI Scenario Coach' : (isDigitalTwin ? 'Digital Twin Avatar' : 'Pitch Assessment'));
  const objective = item.objective || item.pitchTitle || item.title || item.scenarioTitle || item.scriptType || (isScenario ? 'Scenario Competency Simulation' : (isDigitalTwin ? 'Digital Twin Practice Review' : 'Pitchster Practice Review'));
  const status = item.status || 'Active';

  const rawTags = contact?.buyerPersona || item.category || item.tags || [sessionTypeLabel, 'Practice', 'AI Feedback'];
  const tags: string[] = Array.isArray(rawTags) ? rawTags : (typeof rawTags === 'string' ? [rawTags] : [sessionTypeLabel, 'Practice']);

  return (
    <div
      onClick={() => onNavigate(item)}
      style={{
        background: isDark ? '#0d101b' : colors.surface,
        border: `1px solid ${isDigitalTwin ? 'rgba(168, 85, 247, 0.4)' : (isDark ? 'rgba(167, 139, 250, 0.25)' : colors.border)}`,
        borderRadius: 20,
        padding: '22px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: 18,
        cursor: 'pointer',
        transition: 'all 0.25s ease',
        boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.3)' : '0 2px 10px rgba(0,0,0,0.05)',
      }}
      className="group hover:border-purple-400 hover:scale-[1.005]"
    >
      {/* Top Section */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, justifyContent: 'space-between', alignItems: 'flex-start' }}>
        
        {/* Left Column: BUYER PERSONA / PRACTICE HEADER */}
        <div style={{ flex: '1 1 320px', display: 'flex', gap: 14, minWidth: 0 }}>
          {/* User Icon Box */}
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: isDigitalTwin ? 'rgba(168, 85, 247, 0.15)' : (isScenario ? 'rgba(56, 189, 248, 0.15)' : 'rgba(184, 248, 144, 0.15)'),
              border: `1px solid ${isDigitalTwin ? 'rgba(168, 85, 247, 0.3)' : (isScenario ? 'rgba(56, 189, 248, 0.3)' : 'rgba(184, 248, 144, 0.3)')}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              marginTop: 2,
            }}
          >
            {sessionIcon}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            {/* Tag Header: BUYER PERSONA / PRACTICE SESSION */}
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: colors.textMuted }}>
              PRACTICE SESSION · {sessionTypeLabel}
            </div>

            {/* Row 1: Company, Industry, Business Model pills */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: colors.text }}>{company}</span>
              {industry && (
                <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)', color: colors.textMuted }}>
                  {industry}
                </span>
              )}
              {businessModel && (
                <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)', color: colors.textMuted }}>
                  {businessModel}
                </span>
              )}
            </div>

            {/* Rep / Contact Name */}
            <div style={{ fontSize: 17, fontWeight: 700, color: colors.text, margin: '2px 0 0' }}>
              {displayName}
            </div>

            {/* Designation / Role */}
            <div style={{ fontSize: 13, color: colors.textMuted }}>
              {designation}
            </div>

            {/* Email link */}
            <div style={{ fontSize: 13, color: '#A78BFA', wordBreak: 'break-all' }}>
              {email}
            </div>

            {/* Pills: Tags */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
              {tags.map((tag) => (
                <span key={tag} style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 8, background: 'rgba(167, 139, 250, 0.15)', color: '#C4B5FD', border: '1px solid rgba(167, 139, 250, 0.3)' }}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Middle Metadata: TIMESTAMP & DURATION */}
        <div style={{ display: 'flex', gap: 28, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* TIMESTAMP */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: colors.textMuted }}>
              TIMESTAMP
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 700, color: colors.text }}>
              <Calendar className="w-4 h-4 text-purple-400" />
              <span>{dateStr}</span>
            </div>
            <span style={{ fontSize: 12, color: colors.textMuted, marginLeft: 22 }}>{timeStr}</span>
          </div>

          {/* DURATION */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: colors.textMuted }}>
              DURATION
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 700, color: colors.text }}>
              <Clock className="w-4 h-4 text-purple-400" />
              <span>{durationStr}</span>
            </div>
          </div>
        </div>

        {/* Right Score Ring & View Details Button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexShrink: 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: colors.textMuted }}>
              PRACTICE SCORE
            </span>
            
            {/* Score Ring */}
            <div style={{ position: 'relative', width: 62, height: 62, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="62" height="62" viewBox="0 0 62 62" style={{ transform: 'rotate(-90deg)' }}>
                <circle cx="31" cy="31" r="25" fill="none" stroke={isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'} strokeWidth="4" />
                <circle
                  cx="31" cy="31" r="25" fill="none" stroke={scoreColor} strokeWidth="4"
                  strokeDasharray={`${(scoreVal / 100) * 157} 157`} strokeLinecap="round"
                />
              </svg>
              <div style={{ position: 'absolute', fontSize: 15, fontWeight: 800, color: colors.text }}>
                {scoreVal}%
              </div>
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, color: scoreColor }}>{scoreLabel}</span>
          </div>

          {/* View Details Button */}
          <button
            onClick={(e) => { e.stopPropagation(); onNavigate(item); }}
            style={{
              padding: '9px 18px',
              borderRadius: 10,
              border: `1px solid ${isDark ? 'rgba(167, 139, 250, 0.4)' : colors.borderAlt}`,
              background: isDark ? 'rgba(15, 23, 42, 0.8)' : colors.surfaceAlt,
              color: '#A78BFA',
              fontSize: 13,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s ease',
            }}
            className="hover:bg-purple-500/10 hover:border-purple-400"
          >
            View Details →
          </button>
        </div>
      </div>

      {/* Horizontal Divider Line */}
      <div style={{ width: '100%', height: 1, background: isDark ? 'rgba(255, 255, 255, 0.08)' : colors.border }} />

      {/* Bottom Section: PRACTICE TYPE | SIMULATION FOCUS | OBJECTIVE | STATUS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, alignItems: 'center' }}>
        
        {/* Practice Type */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(167, 139, 250, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#A78BFA', flexShrink: 0, marginTop: 2 }}>
            <Repeat className="w-4 h-4" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.1, textTransform: 'uppercase', color: colors.textMuted }}>
              PRACTICE TYPE
            </span>
            <span style={{ fontSize: 14, fontWeight: 700, color: colors.text }}>{sessionTypeLabel}</span>
            <span style={{ fontSize: 11, color: colors.textMuted }}>{salesStage}</span>
          </div>
        </div>

        {/* Focus Mode */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(56, 189, 248, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#38BDF8', flexShrink: 0, marginTop: 2 }}>
            <Target className="w-4 h-4 text-cyan-400" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.1, textTransform: 'uppercase', color: colors.textMuted }}>
              SIMULATION FOCUS
            </span>
            <span style={{ fontSize: 14, fontWeight: 700, color: colors.text }}>{contactType}</span>
            <span style={{ fontSize: 11, color: colors.textMuted }}>AI Evaluation & Scoring</span>
          </div>
        </div>

        {/* Objective */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.1, textTransform: 'uppercase', color: colors.textMuted }}>
            PRACTICE TOPIC
          </span>
          <span style={{ fontSize: 12, fontWeight: 700, padding: '5px 12px', borderRadius: 8, background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', color: colors.text, display: 'inline-block', width: 'fit-content' }}>
            {objective}
          </span>
        </div>

        {/* Status */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.1, textTransform: 'uppercase', color: colors.textMuted }}>
            STATUS
          </span>
          <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: 'rgba(74, 222, 128, 0.12)', color: '#4ADE80', border: '1px solid rgba(74, 222, 128, 0.25)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ADE80' }} />
            {status}
          </span>
        </div>

      </div>
    </div>
  );
};

const isWithinTimeRange = (itemDateStr: string | undefined, timeRange: string): boolean => {
  if (!timeRange || timeRange === 'ALL') return true;
  if (!itemDateStr) return false;
  const itemDate = new Date(itemDateStr);
  if (isNaN(itemDate.getTime())) return true;

  const now = new Date();
  const diffMs = now.getTime() - itemDate.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  if (timeRange === 'today') {
    return itemDate.toDateString() === now.toDateString();
  }
  if (timeRange === '7days') {
    return diffDays <= 7 && diffDays >= 0;
  }
  if (timeRange === '30days') {
    return diffDays <= 30 && diffDays >= 0;
  }
  if (timeRange === '90days') {
    return diffDays <= 90 && diffDays >= 0;
  }
  if (timeRange === 'year') {
    return itemDate.getFullYear() === now.getFullYear();
  }
  return true;
};

export const AllPracticeSessionsPage: React.FC<AllPracticeSessionsPageProps> = ({ onBack, userData, rawMeetings = [] }) => {
  const { isDark, colors } = useDashboardTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRep, setSelectedRep] = useState('ALL');
  const [selectedType, setSelectedType] = useState('ALL');
  const [selectedTime, setSelectedTime] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8;

  const optionStyle = {
    background: isDark ? '#161927' : '#ffffff',
    color: colors.text,
  };

  // Merge practice sessions (Pitches, Scenarios, Digital Twin, Practice Activities, Raw Meetings)
  const allPracticeSessions = useMemo(() => {
    const d = userData?.data || userData || {};
    const pitches = Array.isArray(d.tenantPitchMeta?.items) ? d.tenantPitchMeta.items : (Array.isArray(d.tenantPitchMeta) ? d.tenantPitchMeta : []);
    const scenarios = Array.isArray(d.tenantScenarios) ? d.tenantScenarios : [];
    const activities = Array.isArray(d.recentPracticeActivities) ? d.recentPracticeActivities : (Array.isArray(d.recentTeamActivity) ? d.recentTeamActivity : []);
    const digitalTwins = Array.isArray(d.tenantDigitalTwin) ? d.tenantDigitalTwin : [];
    const meetings = Array.isArray(rawMeetings) ? rawMeetings : [];

    const combined = [
      ...pitches,
      ...scenarios,
      ...digitalTwins,
      ...activities,
      ...meetings,
    ];

    const seen = new Set<string>();
    const unique: any[] = [];

    combined.forEach((item, index) => {
      const id = String(item.id || item._id || item.pitchId || item.scenarioId || item.assignmentId || `practice-${index}`);
      if (!seen.has(id)) {
        seen.add(id);
        const dt = isDigitalTwinPitch(item);
        const sc = isScenarioSession(item);
        unique.push({
          ...item,
          isDigitalTwin: dt,
          isScenario: sc,
          isPitch: !dt && !sc,
          meetingType: dt ? 'digital_twin' : (sc ? 'scenario' : 'pitch'),
        });
      }
    });

    return unique;
  }, [userData, rawMeetings]);

  // Extract all reps list from userData
  const repList = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    const d = userData?.data || userData || {};

    const userSummary = Array.isArray(d.userPitchSummary)
      ? d.userPitchSummary
      : d.userPitchSummary ? Object.values(d.userPitchSummary) : [];

    userSummary.forEach((u: any) => {
      const id = String(u.userId || u.id || u.user_id || '');
      const name = u.name || u.userName || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;
      if (id && name) {
        map.set(id, { id, name });
      }
    });

    const allUsers = Array.isArray(d.tenantUserList?.items) ? d.tenantUserList.items : [];
    allUsers.forEach((u: any) => {
      const id = String(u.id || u.userId || '');
      const name = u.name || u.fullName || u.userName || `${u.firstName || u.first_name || ''} ${u.lastName || u.last_name || ''}`.trim() || u.email;
      if (id && name && (!map.has(id) || map.get(id)?.name === u.email)) {
        map.set(id, { id, name });
      }
    });

    if (d.users && typeof d.users === 'object') {
      Object.keys(d.users).forEach((id) => {
        const u = d.users[id];
        const name = u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;
        if (id && name && !map.has(id)) {
          map.set(id, { id, name });
        }
      });
    }

    if (Array.isArray(d.repScriptStats)) {
      d.repScriptStats.forEach((r: any) => {
        const id = String(r.userId || r.id || '');
        const name = r.userName || r.name;
        if (id && name && !map.has(id)) {
          map.set(id, { id, name });
        }
      });
    }

    allPracticeSessions.forEach((m) => {
      const repId = m.user_id || m.userId || m.createdBy || m.ownerId || m.repId;
      const repName = getRepName(m, userData);
      if (repId && repName && !map.has(String(repId))) {
        map.set(String(repId), { id: String(repId), name: repName });
      }
    });

    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [userData, allPracticeSessions]);

  // Process & Filter practice sessions list
  const filteredSessions = useMemo(() => {
    return allPracticeSessions.filter((m) => {
      const repId = m.user_id || m.userId || m.createdBy || m.ownerId || m.repId;
      const repName = getRepName(m, userData);
      const title = m.pitchTitle || m.title || m.scenarioTitle || m.scriptType || repName || '';

      // Rep filter
      if (selectedRep !== 'ALL') {
        const selectedObj = repList.find((r) => r.id === selectedRep);
        const matchId = String(repId) === String(selectedRep) || String(m.user_id) === String(selectedRep) || String(m.userId) === String(selectedRep) || String(m.repId) === String(selectedRep);
        const matchName = selectedObj && repName.toLowerCase() === selectedObj.name.toLowerCase();
        if (!matchId && !matchName) return false;
      }

      // Type filter (Pitches, Scenarios, Digital Twin)
      if (selectedType !== 'ALL') {
        if (selectedType === 'pitch' && !m.isPitch) return false;
        if (selectedType === 'scenario' && !m.isScenario) return false;
        if (selectedType === 'digital_twin' && !m.isDigitalTwin) return false;
      }

      // Time range filter
      const itemDateStr = m.created_at || m.createdAt || m.date || m.timestamp;
      if (!isWithinTimeRange(itemDateStr, selectedTime)) {
        return false;
      }

      // Search term
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        const match = title.toLowerCase().includes(q) ||
          repName.toLowerCase().includes(q) ||
          String(m.id || m.pitchId || m.scenarioId || '').toLowerCase().includes(q);
        if (!match) return false;
      }
      return true;
    });
  }, [allPracticeSessions, userData, selectedRep, repList, selectedType, selectedTime, searchTerm]);

  // Reset page to 1 when filters change
  const totalPages = Math.max(1, Math.ceil(filteredSessions.length / pageSize));
  const currentFilteredSessions = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredSessions.slice(start, start + pageSize);
  }, [filteredSessions, currentPage, pageSize]);

  const handleCardClick = (item: any) => {
    const id = item.id || item._id || item.pitchId || item.scenarioId || item.meetingId || item.assignmentId;
    navigate(`/meeting-detail/${id}`, {
      state: {
        meeting: item,
        meetingId: id,
        from: location.pathname + location.search,
      },
    });
  };

  const resetFilters = () => {
    setSelectedRep('ALL');
    setSelectedType('ALL');
    setSelectedTime('ALL');
    setSearchTerm('');
    setCurrentPage(1);
  };

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        background: colors.bg,
        height: 'calc(100vh - 70px)',
        maxHeight: 'calc(100vh - 70px)',
        overflow: 'hidden',
        padding: '16px 24px 16px',
        boxSizing: 'border-box',
      }}
    >
      {/* ── Fixed Top: Header & Filter Bar ── */}
      <div style={{ flexShrink: 0 }}>
        {/* Header Bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <button
              onClick={onBack}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10,
                border: `1px solid ${colors.border}`, background: colors.surface, color: colors.text,
                fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}
            >
              <ChevronLeft className="w-4 h-4" /> Back to Dashboard
            </button>
            <div>
              <h1 style={{ fontSize: 24, fontWeight: 700, color: colors.text, margin: 0 }}>All Practice Sessions</h1>
              <p style={{ fontSize: 13, color: colors.textMuted, margin: '2px 0 0' }}>
                Showing {filteredSessions.length} {filteredSessions.length === 1 ? 'practice session' : 'practice sessions'} across reps
              </p>
            </div>
          </div>
        </div>

        {/* Filter Bar */}
        <div
          style={{
            background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 16,
            padding: '14px 18px', marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {/* Search */}
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: colors.textMuted }} className="w-4 h-4" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              placeholder="Search by practice title, rep name..."
              style={{
                width: '100%', borderRadius: 10, border: `1px solid ${colors.borderAlt}`, background: colors.surfaceAlt,
                fontSize: 14, color: colors.text, padding: '9px 12px 9px 36px', outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Rep Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: colors.textMuted, fontWeight: 600 }}>Rep:</span>
            <select
              value={selectedRep}
              onChange={(e) => { setSelectedRep(e.target.value); setCurrentPage(1); }}
              style={{
                borderRadius: 10, border: `1px solid ${colors.borderAlt}`, background: colors.surfaceAlt,
                color: colors.text, fontSize: 14, padding: '9px 14px', outline: 'none', cursor: 'pointer', minWidth: 150,
              }}
            >
              <option value="ALL" style={optionStyle}>All Representatives</option>
              {repList.map((r) => (
                <option key={r.id} value={r.id} style={optionStyle}>{r.name}</option>
              ))}
            </select>
          </div>

          {/* Type Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: colors.textMuted, fontWeight: 600 }}>Type:</span>
            <select
              value={selectedType}
              onChange={(e) => { setSelectedType(e.target.value); setCurrentPage(1); }}
              style={{
                borderRadius: 10, border: `1px solid ${colors.borderAlt}`, background: colors.surfaceAlt,
                color: colors.text, fontSize: 14, padding: '9px 14px', outline: 'none', cursor: 'pointer', minWidth: 140,
              }}
            >
              <option value="ALL" style={optionStyle}>All Practice Types</option>
              <option value="pitch" style={optionStyle}>Sales Pitches</option>
              <option value="scenario" style={optionStyle}>Guided Scenarios</option>
              <option value="digital_twin" style={optionStyle}>Digital Twin</option>
            </select>
          </div>

          {/* Time / Date Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: colors.textMuted, fontWeight: 600 }}>Time:</span>
            <select
              value={selectedTime}
              onChange={(e) => { setSelectedTime(e.target.value); setCurrentPage(1); }}
              style={{
                borderRadius: 10, border: `1px solid ${colors.borderAlt}`, background: colors.surfaceAlt,
                color: colors.text, fontSize: 14, padding: '9px 14px', outline: 'none', cursor: 'pointer', minWidth: 130,
              }}
            >
              <option value="ALL" style={optionStyle}>All Time</option>
              <option value="today" style={optionStyle}>Today</option>
              <option value="7days" style={optionStyle}>Last 7 Days</option>
              <option value="30days" style={optionStyle}>Last 30 Days</option>
              <option value="90days" style={optionStyle}>Last 90 Days</option>
              <option value="year" style={optionStyle}>This Year</option>
            </select>
          </div>

          {/* Reset */}
          {(selectedRep !== 'ALL' || selectedType !== 'ALL' || selectedTime !== 'ALL' || searchTerm !== '') && (
            <button
              onClick={resetFilters}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 10,
                border: `1px solid ${colors.borderAlt}`, background: 'rgba(248,113,113,0.1)', color: '#F87171',
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
          )}
        </div>
      </div>

      {/* ── Scrollable Middle: Practice Sessions List ── */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          minHeight: 0,
          paddingRight: 6,
          marginBottom: 12,
        }}
      >
        {currentFilteredSessions.length === 0 ? (
          <div
            style={{
              height: '100%', minHeight: 280, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 16,
              padding: 40, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <div style={{ fontSize: 40, marginBottom: 12 }}>🎯</div>
            <h3 style={{ fontSize: 18, color: colors.text, fontWeight: 600, margin: '0 0 6px' }}>No practice sessions found</h3>
            <p style={{ fontSize: 14, color: colors.textMuted, margin: '0 0 16px' }}>Try adjusting your filters or search terms</p>
            <button
              onClick={resetFilters}
              style={{
                padding: '8px 18px', borderRadius: 10, border: 'none', background: isDark ? '#A78BFA' : '#7c3aed',
                color: '#ffffff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
              }}
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingBottom: 6 }}>
            {currentFilteredSessions.map((item) => (
              <PersonaPracticeCard
                key={item.id || item._id || item.pitchId || item.scenarioId || item.assignmentId}
                item={item}
                userData={userData}
                onNavigate={handleCardClick}
                colors={colors}
                isDark={isDark}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Fixed Bottom: Pagination Bar ── */}
      {totalPages > 1 && (
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 14, padding: '12px 20px', marginTop: 'auto' }}>
          <span style={{ fontSize: 13, color: colors.textMuted }}>
            Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, filteredSessions.length)} of {filteredSessions.length}
          </span>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              style={{
                display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 8,
                border: `1px solid ${colors.borderAlt}`, background: colors.surfaceAlt, color: colors.text,
                fontSize: 13, fontWeight: 600, cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1,
              }}
            >
              <ChevronLeft className="w-4 h-4" /> Previous
            </button>

            <span style={{ fontSize: 13, fontWeight: 700, color: colors.text, padding: '0 8px' }}>
              Page {currentPage} of {totalPages}
            </span>

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              style={{
                display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 8,
                border: `1px solid ${colors.borderAlt}`, background: colors.surfaceAlt, color: colors.text,
                fontSize: 13, fontWeight: 600, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1,
              }}
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllPracticeSessionsPage;
